import {Box, TextField, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {ReactComponent as DownIcon} from 'assets/icons/icon_down.svg';
import {ReactComponent as SearchIcon} from 'assets/icons/icon_search.svg';
import cn from 'classnames';
import {EntityType} from 'components/EntityType/EntityType';
import {useDropdown} from 'hooks/useDropdown';
import {useEffect, useMemo, useState} from 'react';

import {useDropdownOptions} from '../../../../../hooks/useDropdownOptions';
import {capitalizeWords} from '../../../../../utils/string';
import styles from './SearchNodes.module.css';

const nodeTypes = {
  person: {title: 'Persons'},
  vessel: {title: 'Vessels'},
  company: {title: 'Companies'}
};

export const SearchNodes = ({nodes, selectedNode, onSelectNode}) => {
  const [dropdownOpenState, setDropdownOpenState] = useState({
    person: true,
    vessel: true,
    company: true
  });
  const {isOpen, setIsOpen, dropdownRef} = useDropdown();
  const [search, setSearch] = useState('');
  const theme = useTheme();

  const groupedNodes = useMemo(() => {
    const filteredNodes = nodes.filter(({data}) => data.label.toLowerCase().includes(search.toLowerCase()));

    return {
      person: filteredNodes.filter(({data}) => data.entityType === 'person'),
      vessel: filteredNodes.filter(({data}) => data.entityType === 'vessel'),
      company: filteredNodes.filter(({data}) => data.entityType === 'company')
    };
  }, [nodes, search]);

  Object.values(groupedNodes)
    .flat()
    .forEach((node, index) => {
      node.index = index;
    });

  const {handleKeyDown, highlightedIndex, setHighlightedIndex} = useDropdownOptions({
    isOpen,
    onSelect: (index) => {
      onSelectNode?.(Object.values(groupedNodes).flat()[index]);
    },
    count: Object.values(groupedNodes).flat().length,
    onClose: () => setIsOpen(false)
  });

  const onSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setHighlightedIndex(0);
  };

  useEffect(() => {
    const block = dropdownRef.current;
    if (block) {
      block.querySelectorAll('.nodeItem')[highlightedIndex]?.scrollIntoView({block: 'nearest'});
    }
  }, [highlightedIndex]);

  const handleSelectNode = (node) => () => {
    onSelectNode?.(node);
  };

  const onClear = () => {
    onSelectNode?.(null);
  };

  const handleDropdown = (type) => () => {
    setDropdownOpenState((prev) => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  return (
    <Box ref={dropdownRef} sx={{background: theme.palette.white.main}} className={styles.container}>
      <TextField
        placeholder="Find Entities"
        InputProps={{startAdornment: <SearchIcon className={styles.searchIcon} style={{fill: theme.palette.secondary.main}} />}}
        sx={{color: theme.palette.gray.gray100}}
        variant="outlined"
        onChange={onSearch}
        className={styles.textField}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={() => setHighlightedIndex(-1)}
      />
      <Box
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        className={cn(styles.dropdown, isOpen && styles.dropdownOpen)}
      >
        {!!selectedNode && (
          <Box sx={{borderBottom: `1px solid ${theme.palette.gray.gray100}`}} className={styles.selectionBlock}>
            <Box className={styles.selectionTitle}>
              <Typography color="secondary" className={styles.currentSelection}>
                Current Selection:
              </Typography>
              <Typography title={capitalizeWords(selectedNode.data.label)} className={cn(styles.selectedNodeLabel, 'truncated')}>
                {capitalizeWords(selectedNode.data.label)}
              </Typography>
            </Box>
            <button style={{color: theme.palette.secondary.secondary200}} className={styles.clear} onClick={onClear}>
              Clear
            </button>
          </Box>
        )}
        {Object.entries(groupedNodes).map(([type, nodes]) => (
          <Box key={type}>
            <button onClick={handleDropdown(type)} className={styles.dropdownButton}>
              <div className={styles.dropdownButtonLabelBlock}>
                <EntityType entityType={type} disableLabel />
                <Typography color="secondary" className={styles.dropdownLabel}>
                  {nodeTypes[type].title}
                </Typography>
              </div>
              <DownIcon className={cn(styles.dropdownIcon, dropdownOpenState[type] && styles.dropdownIconOpen)} />
            </button>
            {!!dropdownOpenState[type] && (
              <ul className={styles.nodes}>
                {nodes.map((node) => (
                  <li
                    tabIndex={1}
                    onClick={handleSelectNode(node)}
                    className={cn(
                      'nodeItem',
                      styles.nodeItem,
                      'truncated',
                      selectedNode?.data?.id === node.data.id && styles.nodeItemSelected,
                      node.index === highlightedIndex && styles.nodeItemHighlighted
                    )}
                    key={node.data.id}
                  >
                    {node.data.label.toLowerCase()}
                  </li>
                ))}
              </ul>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
